import { connect } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { ErknmSubjectAdd } from 'KnmErknmPackage/ErknmSubjectAdd/components/ErknmSubjectAdd';
import {
  getActDocumentAttachmentsBlockId,
  getActInnLabelBlockId,
  getActOgrnLabelBlockId,
  getActOrganizationNameBlockId,
  getActOrganizationTemplateBlockId,
  getActNumberBlockId,
  getActCreationActDateTimeBlockId,
  getActHoldingDateTimeBlockId,
  getActDurationHours,
  getActDurationDays,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { KnmLateFieldsRemark } from 'KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { getKnmStartDate } from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import { ErknmActDocument } from '../ErknmActDocument';
import styles from '../ErknmActsItem.module.scss';
import { getKnmDataCreated, getKnmFormRef } from '../../../../KnmPackage/MainModule/selectors/knmSelectors';
import { isLaterThanAdminSetting } from '../../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IDictionarySelectOption } from '../../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IParticipantPosition } from '../../../../APIPackage/DictionariesModule/interfaces/IParticipantPosition';
import { ErknmFieldMark } from '../../../MainModule/components/ErknmFieldMark';
import { ErknmActsItemContentKnoInspectors } from './components/ErknmActsItemContentKnoInspectors';
import { ErknmActsItemContentViolatedMandatoryRequirements } from './components/ErknmActsItemContentViolatedMandatoryRequirements';
import { ErknmActsItemContentAcquaintance } from './components/ErknmActsItemContentAcquaintance';
import { ErknmActsItemContentEvents } from './components/Events/ErknmActsItemContentEvents';
import { IKnmErknmFormEvent } from '../../../MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormExpert } from '../../../MainModule/interfaces/IKnmErknmFormExpert';
import { Signers } from '../../../../KnmPackage/SignersModule/components/Signers';

interface IErknmActsItemProps {
  addRequirementsNpa: (actFieldName: string, fieldName: string) => void;
  addViolatedMandatory: (initialValue: IRequirementsNpaInitValues, fieldName: string) => void;
  events: IKnmErknmFormEvent[] | undefined;
  expertsErknm?: IKnmErknmFormExpert[];
  id: string;
  index: number;
  inspectorsErknm: IKnmErknmFormInspector[] | undefined;
  isDisabledByComplete: boolean;
  isWatchOnly: boolean;
  kindKnmCode: KnmKindCodesEnum | undefined;
  lateFieldName: string;
  name: string;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  participantPositionsSelectOptions: IDictionarySelectOption<IParticipantPosition>[] | undefined;
  value: IKnmErknmFormAct;
}

interface IErknmActsItemStoreProps {
  created: string | null | undefined;
  formRef: IKnmFormRef | undefined;
  settings: IAdminSetting[] | undefined;
  startDate: string | undefined;
}

const ErknmActsItemContentKnmWithoutStore = ({
  addRequirementsNpa,
  addViolatedMandatory,
  events,
  expertsErknm,
  id,
  index,
  inspectorsErknm,
  isWatchOnly,
  kindKnmCode,
  name,
  organizationsTemplatesSelectOptions,
  participantPositionsSelectOptions,
  value,
  created,
  formRef,
  settings,
  isDisabledByComplete,
  lateFieldName,
  startDate,
}: IErknmActsItemProps & IErknmActsItemStoreProps) => {
  const { isNew: isNewAct, organizationTemplate, organization, titleSigner } = value;
  const [knmActType, setKnmActTypeSelected] = useState<number | null>(null);

  const documentAttachmentsBlockId = getActDocumentAttachmentsBlockId(id);
  const isNewOrganizationTemplate = !!organizationTemplate && !organizationTemplate.value;
  const actOrganizationsTemplatesSelectOptions:
    | IKnmErknmOrganizationTemplateSelectOption[]
    | undefined = organizationsTemplatesSelectOptions
    ?.filter(organizationTemplateSelectOption => {
      const isSelectedOrganizationTemplate =
        organizationTemplateSelectOption.id === organizationTemplate?.id ||
        organizationTemplateSelectOption.id === organization?.listId ||
        organizationTemplateSelectOption.value === organizationTemplate?.value;

      return (
        !(isNewOrganizationTemplate && organizationTemplateSelectOption.id === organization?.listId) &&
        (isSelectedOrganizationTemplate ||
          !organizationTemplateSelectOption.inspectionResults?.find(
            i => i.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
          ))
      );
    })
    .map(organizationTemplateSelectOption => ({
      ...organizationTemplateSelectOption,
      isDisabled: formRef?.current
        .getFieldState('acts')
        ?.value?.some(
          actsItem =>
            organizationTemplateSelectOption.value &&
            actsItem?.organization?.listId === organizationTemplateSelectOption.id &&
            organization?.listId !== organizationTemplateSelectOption.id,
        ),
    }));

  const isShow4127 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_27_STARTS_FROM,
  );

  const isUseSigners = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ADD_NEW_SIGNER_199_4_1_6_STARTS_FROM,
  );

  const isDisabledField = isWatchOnly || isDisabledByComplete;

  useEffect(() => {
    if (value.typeAct) {
      setKnmActTypeSelected(value.typeAct.id);
    }
  }, [value]);

  const isAvailableSingerPosition = !!participantPositionsSelectOptions?.find(
    participant => participant.id === titleSigner?.id,
  );

  /**
   * Список вариантов для выбора должностного лица, подписавшего акт
   */
  const inspectorsErknmOptions = useMemo(() => {
    return (
      inspectorsErknm?.map(inspector => {
        return {
          id: inspector.guid || '',
          value: inspector,
          label: `${inspector.fullName} - ${inspector.position?.label}`,
        };
      }) || []
    );
  }, [inspectorsErknm]);

  const parseStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

  const renderSignersBlock = () => {
    if (isUseSigners) {
      return (
        <Signers
          formRef={formRef}
          hasActOrResultDecisionFirstExtractDateTime={!!value?.firstExtractDateTime}
          hintText={
            value?.isNew
              ? 'Выбор должностного лица из раздела "Сведения о должностных лицах, участвующих в КНМ'
              : 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ» или добавить вручную после подписания акта'
          }
          inspectors={isShow4127 ? inspectorsErknm : undefined}
          isActOrResultDecisionSigned={value?.signed === true}
          isKnm={true}
          isNewActOrResultDecision={value?.isNew === true}
          label="Подпись акта"
          parentId={id}
          parentName={name}
          positionsSelectOptions={participantPositionsSelectOptions}
        />
      );
    }

    return isShow4127 ? (
      <Row>
        <FieldBlock
          id={`${id}.fioAndPositionSinger`}
          label={
            <>
              ФИО и должность лица, подписавшего акт
              <ErknmFieldMark fieldName={`${name}.fioAndPositionSinger`} />
            </>
          }
          margin="top"
          tooltip={
            <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/fioSigner`} fieldId={`${id}.fioAndPositionSinger`} />
          }
        >
          <SelectInputField
            name={`${name}.fioAndPositionSinger`}
            placeholder="Выберите должностное лицо"
            options={inspectorsErknmOptions}
            disabled={isDisabledField}
            size="medium"
          />
        </FieldBlock>
      </Row>
    ) : (
      <Row>
        <FieldBlock
          label={'ФИО подписавшего акт'}
          size="half"
          color="black"
          id={`${id}.fioSigner`}
          tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/fioSigner`} fieldId={`${id}.fioSigner`} />}
        >
          <TextInputField name={`${name}.fioSigner`} disabled={isDisabledField} />
        </FieldBlock>
        <FieldBlock
          label={'Должность лица, подписавшего акт'}
          size="half"
          color="black"
          id={`${id}.titleSigner`}
          tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/titleSigner`} fieldId={`${id}.titleSigner`} />}
        >
          <SelectInputField
            name={`${name}.titleSigner`}
            placeholder="Выберите должность"
            options={participantPositionsSelectOptions}
            isSearchable
            // eslint-disable-next-line no-nested-ternary
            disabled={titleSigner ? (isAvailableSingerPosition ? isDisabledField : false) : isDisabledField}
            size="medium"
          />
        </FieldBlock>
      </Row>
    );
  };

  return (
    <>
      <FieldBlock id={documentAttachmentsBlockId}>
        <ErknmActDocument
          isWatchOnly={isDisabledField}
          name={`${name}.document.attachments`}
          attachmentsNumber={value.document?.attachments?.length || 0}
          id={documentAttachmentsBlockId}
          index={index}
          formRef={formRef}
          lateFieldName={
            value.document?.attachments && value.document?.attachments[0]?.guid
              ? `${lateFieldName}/document/attachments/${value.document?.attachments[0]?.guid}/fileName`
              : undefined
          }
        />
      </FieldBlock>
      <Row>
        <FieldBlock
          label={
            <>
              Номер акта
              <ErknmFieldMark fieldName={`${name}.numberAct`} />
            </>
          }
          color="black"
          placementLabel="left"
          id={getActNumberBlockId(value.listId)}
          tooltip={
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/numberAct`}
              fieldId={getActNumberBlockId(value.listId)}
            />
          }
        >
          <TextInputField name={`${name}.numberAct`} disabled={isDisabledField} className="shared-row-field" />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          label={
            <>
              Дата и время составления акта
              <ErknmFieldMark fieldName={`${name}.creationActDateTime`} />
            </>
          }
          color="black"
          placementLabel="left"
          type="not-space"
          id={getActCreationActDateTimeBlockId(value.listId)}
          tooltip={
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/creationActDateTime`}
              fieldId={getActCreationActDateTimeBlockId(value.listId)}
            />
          }
        >
          <DatePickerField
            className={styles.DatePickerField}
            name={`${name}.creationActDateTime`}
            disabled={isDisabledField}
            timeFormat
          />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          label={
            <>
              Дата и время начала проведения
              <ErknmFieldMark fieldName={`${name}.holdingDateTime`} />
            </>
          }
          color="black"
          placementLabel="left"
          type="not-space"
          id={getActHoldingDateTimeBlockId(value.listId)}
          tooltip={
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/holdingDateTime`}
              fieldId={getActHoldingDateTimeBlockId(value.listId)}
            />
          }
        >
          <DatePickerField
            className={styles.DatePickerField}
            name={`${name}.holdingDateTime`}
            disabled={isDisabledField}
            timeFormat
            minDate={parseStartDate}
          />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          label={
            <>
              Срок проведения (в днях)
              <ErknmFieldMark fieldName={`${name}.durationDays`} />
            </>
          }
          size="half"
          color="black"
          id={getActDurationDays(value.listId)}
          tooltip={
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/durationDays`}
              fieldId={getActDurationDays(value.listId)}
            />
          }
        >
          <TextInputField name={`${name}.durationDays`} disabled={isDisabledField} />
        </FieldBlock>
        <FieldBlock
          label={
            <>
              Срок проведения (в часах)
              <ErknmFieldMark fieldName={`${name}.durationHours`} />
            </>
          }
          size="half"
          color="black"
          id={getActDurationHours(value.listId)}
          tooltip={
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/durationHours`}
              fieldId={getActDurationHours(value.listId)}
            />
          }
        >
          <TextInputField name={`${name}.durationHours`} disabled={isDisabledField} />
        </FieldBlock>
      </Row>
      {renderSignersBlock()}
      {(kindKnmCode === KnmKindCodesEnum.RO || !isNewAct) && (
        <div className={styles.SubjectAdd}>
          <ErknmSubjectAdd
            id={id}
            watchOnly={isDisabledField}
            kindKnmCode={kindKnmCode}
            organizationsTemplatesSelectOptions={actOrganizationsTemplatesSelectOptions}
            name={name}
            organizationTemplate={value.organizationTemplate}
            organizationTemplateBlockId={getActOrganizationTemplateBlockId(value.listId)}
            inn={value.organization?.inn}
            innLabelBlockId={getActInnLabelBlockId(id)}
            ogrn={value.organization?.ogrn}
            ogrnLabelBlockId={getActOgrnLabelBlockId(id)}
            firstOrganizationName={value.organization?.firstOrganizationName}
            organizationNameBlockId={getActOrganizationNameBlockId(id)}
            checkSubjectResult={value.organization?.checkSubjectResult || null}
            isAutocomplete={value.organization?.isAutocomplete}
            subjectType={value.organization?.subjectType}
            kpp={value.organization?.kpp}
            smevRequestStatus={value.organization?.smevRequestStatus}
            rafpNza={value.organization?.rafpNza}
            isSonko={value.organization?.isSonko}
            snils={value.organization?.snils}
            ervkId={value.organization?.ervkId}
            isPresentInItRegistry={value.organization?.isPresentInItRegistry}
            itAccreditationResponse={value.organization?.itAccreditationResponse}
            smevItAccreditationRequestStatus={value.organization?.smevItAccreditationRequestStatus}
            okveds={value.organization?.okveds}
            mspCode={value.organization?.mspCode}
            mspRegistryDate={value.organization?.mspRegistryDate}
          />
        </div>
      )}

      <ErknmActsItemContentKnoInspectors
        id={id}
        index={index}
        inspectorsErknm={inspectorsErknm}
        isWatchOnly={isWatchOnly}
        name={name}
        participantPositionsSelectOptions={participantPositionsSelectOptions}
        isDisabledByComplete={isDisabledByComplete}
        lateFieldName={lateFieldName}
      />

      <ErknmActsItemContentEvents
        expertsErknm={expertsErknm}
        id={id}
        isWatchOnly={isWatchOnly}
        name={name}
        isDisabledByComplete={isDisabledByComplete}
        lateFieldName={lateFieldName}
        index={index}
        selectedKnmEventTypes={events?.filter(event => event.type).map(event => event.type)}
      />

      <ErknmActsItemContentViolatedMandatoryRequirements
        addRequirementsNpa={addRequirementsNpa}
        addViolatedMandatory={addViolatedMandatory}
        id={id}
        index={index}
        isWatchOnly={isWatchOnly}
        name={name}
        value={value}
        isDisabledByComplete={isDisabledByComplete}
        knmActType={knmActType}
        lateFieldName={lateFieldName}
      />

      <ErknmActsItemContentAcquaintance
        id={id}
        isDisabledField={isDisabledField}
        name={name}
        value={value}
        lateFieldName={lateFieldName}
      />

      <FieldBlock
        label={
          <>
            Сведения о признании недействительными результатов КНМ
            <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/noteInvalidate`} fieldId={`${id}.noteInvalidate`} />
          </>
        }
        id={`${id}.noteInvalidate`}
      >
        <TextareaField className={styles.InvalidityField} name={`${name}.noteInvalidate`} disabled={isDisabledField} />
      </FieldBlock>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmActsItemStoreProps => ({
  created: getKnmDataCreated(state),
  formRef: getKnmFormRef(state),
  settings: getAdminFetchSettings(state),
  startDate: getKnmStartDate(state),
});

export const ErknmActsItemContentKnm = connect(mapStateToProps)(ErknmActsItemContentKnmWithoutStore);
